import {
  Component,
  h,
  Element,
  Listen,
  State,
  Method,
  Prop,
} from "@stencil/core"
import {
  menuFlyoutReducer,
  getInitialState,
  MenuFlyoutActions,
  MenuFlyoutState,
} from "../menu-flyout-store"
import {
  StoreConnection,
  StateHandle,
  translate,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"
import { BackgroundTheme } from "../../../core/types/types"

@Component({
  tag: "sdx-menu-flyout-toggle",
  styleUrl: "menu-flyout-toggle.scss",
  shadow: true,
})
export class MenuFlyoutToggle {
  private store: StoreConnection<this, MenuFlyoutState, MenuFlyoutActions>
  private buttonEl?: HTMLButtonElement

  @Element() public el!: HTMLSdxMenuFlyoutToggleElement

  @State() public state!: StateHandle<MenuFlyoutState>

  /**
   * Description text read by the screen reader.
   */
  @Prop() public srHint: string = translate("Open dialog", this.el.lang)

  /**
   * Background theme. Use "dark" when placed on a gradient background.
   */
  @Prop() public background: BackgroundTheme = "light"

  /**
   * Sets the focus on the component.
   */
  @Method()
  public async doFocus() {
    this.buttonEl?.focus()
  }

  @Listen("click")
  public onClick() {
    this.store.set("isUserInteractionInProgress", true)
    this.state.get().toggle()
    this.store.set("isUserInteractionInProgress", false)
  }

  @Listen("keydown")
  public handleKeyDown(e: KeyboardEvent) {
    const key = e.key

    if (key === "Spacebar" || key === " " || key === "Enter") {
      e.preventDefault() // prevent scrolling, for space
      this.store.set("isUserInteractionInProgress", true)
      this.state.get().toggle()
      this.store.set("isUserInteractionInProgress", false)
    }
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      menuFlyoutReducer,
      getInitialState(),
      ["display", "toggle"],
    )
  }

  public connectedCallback() {
    // Register self
    this.store.set("toggleEl", this.el)
  }

  public disconnectedCallback() {
    // Unregister self
    this.store.set("toggleEl", undefined)
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private getButtonClassNames() {
    return {
      "button-reset": true,
      toggle: true,
      [this.background]: true,
      [getAppearance(this.el)]: true,
    }
  }

  public render() {
    return (
      <button
        type="button"
        class={this.getButtonClassNames()}
        ref={(el) => (this.buttonEl = el)}
        aria-label={this.srHint}
        aria-expanded={(this.state.get().display === "open").toString()}
      >
        <slot />
      </button>
    )
  }
}
